/* eslint-disable no-mixed-spaces-and-tabs */
import { createSelector } from 'reselect';
import { getFormValues, isInvalid } from 'redux-form';
import { FORM_NAME } from './constants';

const selectOrderGroupsModalDomain = state => state.orderGroupsPlanningContainerReducer || {};

export const getFormIsInvalidSelector = () =>
	createSelector(isInvalid('orders-planning-modal'), isInvalid => isInvalid);

export const getConfigsSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ configs }) => configs?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsConfigsLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isConfigsLoading }) => isConfigsLoading);

export const getTransportTypesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ transportTypes }) => transportTypes?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsTransportTypesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isTransportTypesLoading }) => isTransportTypesLoading);

export const getIsOrderGroupInfoLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isOrderGroupInfoLoading }) => isOrderGroupInfoLoading);

export const getFormInitialValuesSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ orderGroupInfo }) => {
		if (!orderGroupInfo) {
			return {
				is_weekend_drop: true,
				is_traffic_jams: true,
			};
		}

		return {
			config_id: orderGroupInfo?.config?.id,
			is_autopark: orderGroupInfo.is_autopark,
			autopark_ids: orderGroupInfo?.autoparks?.map(el => el.id),
			tariff_type_id: orderGroupInfo?.tariff_type?.id,
			transport_type_id: orderGroupInfo?.transport_type?.id,
			transport_count: orderGroupInfo.transport_count,
			weight: orderGroupInfo.weight,
			volume: orderGroupInfo.volume,
			weight_recycling_rate: orderGroupInfo.weight_recycling_rate,
			volume_recycling_rate: orderGroupInfo.volume_recycling_rate,
			weight_rate: orderGroupInfo.weight_rate,
			volume_rate: orderGroupInfo.volume_rate,
			points_amount: orderGroupInfo.points_amount,
			speed_multiplier: orderGroupInfo.speed_multiplier,
			work_time_from: orderGroupInfo.work_time_from,
			work_time_to: orderGroupInfo.work_time_to,
			pickup_time_from: orderGroupInfo.pickup_time_from,
			pickup_time_to: orderGroupInfo.pickup_time_to,
			pickup_time: orderGroupInfo.pickup_time,
			drop_time_from: orderGroupInfo.drop_time_from,
			drop_time_to: orderGroupInfo.drop_time_to,
			drop_time: orderGroupInfo.drop_time,
			is_compatibility_mode: orderGroupInfo.is_compatibility_mode,
			is_refund: orderGroupInfo.is_refund,
			is_pickup_day_to_day: orderGroupInfo.is_pickup_day_to_day,
			is_flexible_date: orderGroupInfo.is_flexible_date,
			is_ship_per_car: orderGroupInfo.is_ship_per_car,
			is_allow_way: orderGroupInfo.is_allow_way,
			penalty_cost: orderGroupInfo.penalty_cost,
			is_weekend_pickup: orderGroupInfo.is_pickup_day_to_day ? true : orderGroupInfo.is_weekend_pickup,
			is_weekend_drop: !orderGroupInfo.is_flexible_date ? true : orderGroupInfo.is_weekend_drop,
			is_traffic_jams: orderGroupInfo.is_traffic_jams,
			is_toll_roads: orderGroupInfo.is_toll_roads,
			is_start_date: orderGroupInfo.is_start_date,
			start_date: orderGroupInfo.start_date,
			is_created_date: orderGroupInfo.is_created_date,
			build_days: orderGroupInfo.build_days,
			compatibilities:
				orderGroupInfo.compatibilities.map(compatibility => ({
					mode_id: compatibility.mode.id,
					compatibility: compatibility.compatibility.map(el => el.id),
				})) ?? [],
		};
	});

export const getDataForCreateOrUpdateSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => {
		return {
			config_id: formValues.config_id,
			is_autopark: formValues.is_autopark,
			autopark_ids: formValues.is_autopark ? formValues.autopark_ids : null,
			tariff_type_id: formValues.is_autopark ? formValues.tariff_type_id : null,
			transport_type_id: !formValues.is_autopark ? formValues.transport_type_id : null,
			transport_count: !formValues.is_autopark ? formValues.transport_count : null,
			weight: !formValues.is_autopark ? formValues.weight : null,
			volume: !formValues.is_autopark ? formValues.volume : null,
			weight_recycling_rate: formValues.weight_recycling_rate,
			volume_recycling_rate: formValues.volume_recycling_rate,
			weight_rate: formValues.weight_rate,
			volume_rate: formValues.volume_rate,
			points_amount: formValues.points_amount,
			speed_multiplier: formValues.speed_multiplier,
			work_time_from: formValues.work_time_from,
			work_time_to: formValues.work_time_to,
			pickup_time_from: formValues.pickup_time_from,
			pickup_time_to: formValues.pickup_time_to,
			pickup_time: formValues.pickup_time,
			drop_time_from: formValues.drop_time_from,
			drop_time_to: formValues.drop_time_to,
			drop_time: formValues.drop_time,
			is_compatibility_mode: formValues.is_compatibility_mode,
			is_refund: formValues.is_refund,
			is_pickup_day_to_day: formValues.is_pickup_day_to_day,
			is_flexible_date: formValues.is_flexible_date,
			is_ship_per_car: formValues.is_ship_per_car,
			is_allow_way: formValues.is_allow_way,
			penalty_cost: formValues.penalty_cost,
			is_weekend_pickup: formValues.is_weekend_pickup,
			is_weekend_drop: formValues.is_weekend_drop,
			is_traffic_jams: formValues.is_traffic_jams,
			is_toll_roads: formValues.is_toll_roads,
			is_start_date: formValues.is_start_date,
			start_date: formValues.start_date,
			is_created_date: formValues.is_created_date,
			build_days: formValues.build_days,
			compatibilities: formValues.compatibilities ?? [],
		};
	});

export const getIsPlanningLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isPlanningLoading }) => isPlanningLoading);

export const getFormValuesSelector = () =>
	createSelector(getFormValues(FORM_NAME), formValues => ({
		...formValues,
	}));

export const getModesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ modes }) => modes?.map(m => ({ value: m.id, text: m.name_with_symbol, name: m.name_with_symbol })) || [],
	);

export const getIsModesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isModesLoading }) => isModesLoading);

export const getTariffTypesSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ tariffTypes }) => tariffTypes?.map(t => ({ value: t.id, text: t.name, name: t.name })) || [],
	);

export const getIsTariffTypesLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isTariffTypesLoading }) => isTariffTypesLoading);

export const getAutoparksSelector = () =>
	createSelector(
		selectOrderGroupsModalDomain,
		({ autoparks }) =>
			autoparks?.map(t => ({
				value: t.id,
				text: `${t.tk.name}${t.name ? `, ${t.name}` : ''}`,
				name: `${t.tk.name}${t.name ? `, ${t.name}` : ''}`,
			})) || [],
	);

export const getIsAutoparksLoadingSelector = () =>
	createSelector(selectOrderGroupsModalDomain, ({ isAutoparksLoading }) => isAutoparksLoading);
